import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { ACTIONS, ACTION_LABEL, MODULE_GROUPS, PAGE_ITEMS, emptyModulePerms } from "@/lib/permissionsSchema";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FieldsPicker } from "@/components/permissions/FieldsPicker";
import { Save } from "lucide-react";
import { toast } from "sonner";

function ModuleRow({ label, collection, perms, onChange, disabled }) {
  const p = perms || emptyModulePerms();
  const set = (patch) => onChange({ ...p, ...patch });
  return (
    <div className="grid grid-cols-[1fr_repeat(4,64px)_auto] items-center gap-2 py-2 px-3 border-b border-border last:border-b-0 text-sm">
      <span className="truncate">{label}</span>
      {ACTIONS.map((a) => (
        <div key={a} className="flex justify-center">
          <Checkbox checked={!!p[a]} onCheckedChange={(v) => set({ [a]: !!v })} disabled={disabled} data-testid={`perm-${collection}-${a}`} />
        </div>
      ))}
      <FieldsPicker collection={collection} value={p.hidden_fields} onChange={(hf) => set({ hidden_fields: hf })} disabled={disabled} />
    </div>
  );
}

function ModuleTableHeader() {
  return (
    <div className="grid grid-cols-[1fr_repeat(4,64px)_auto] items-center gap-2 py-2 px-3 border-b border-border bg-muted/40 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
      <span>Module</span>
      {ACTIONS.map((a) => <span key={a} className="text-center">{ACTION_LABEL[a]}</span>)}
      <span className="text-right pr-1">Hidden fields</span>
    </div>
  );
}

export function RolePermissionsPanel() {
  const [roles, setRoles] = useState([]);
  const [roleDocs, setRoleDocs] = useState({});
  const [selectedRole, setSelectedRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [schemaRes, rolesRes] = await Promise.all([
        api.get("/permissions/schema"),
        api.get("/permissions/roles"),
      ]);
      setRoles(schemaRes.data.roles || []);
      setRoleDocs(rolesRes.data || {});
      setSelectedRole((prev) => prev || (schemaRes.data.roles || [])[0] || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const currentRoleDoc = roleDocs[selectedRole] || { modules: {}, pages: {} };
  const isSuperAdminRole = selectedRole === "SuperAdmin";

  const setModulePerms = (collection, perms) => {
    setRoleDocs((prev) => ({
      ...prev,
      [selectedRole]: { ...prev[selectedRole], modules: { ...prev[selectedRole]?.modules, [collection]: perms } },
    }));
  };
  const setPageView = (page, view) => {
    setRoleDocs((prev) => ({
      ...prev,
      [selectedRole]: { ...prev[selectedRole], pages: { ...prev[selectedRole]?.pages, [page]: { view } } },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const doc = roleDocs[selectedRole];
      await api.put(`/permissions/roles/${selectedRole}`, { modules: doc.modules, pages: doc.pages });
      toast.success(`${selectedRole} permissions saved`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-96 rounded-xl" />;

  return (
    <div className="space-y-4">
      <Card className="p-4 rounded-xl border border-border bg-card flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Role</span>
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger className="w-56" data-testid="role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          {isSuperAdminRole && (
            <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">
              Always has full access — not editable
            </Badge>
          )}
        </div>
        <Button onClick={save} disabled={saving || isSuperAdminRole} className="kn-gradient-bg text-white border-0" data-testid="save-role-permissions">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </Card>

      <Card className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <p className="font-display text-sm font-semibold">Pages</p>
        </div>
        <div className="divide-y divide-border">
          {PAGE_ITEMS.map((p) => (
            <div key={p.key} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span>{p.label}</span>
              <Switch
                checked={!!currentRoleDoc.pages?.[p.key]?.view}
                onCheckedChange={(v) => setPageView(p.key, v)}
                disabled={isSuperAdminRole}
                data-testid={`page-${p.key}-view`}
              />
            </div>
          ))}
        </div>
      </Card>

      {MODULE_GROUPS.map((group) => (
        <Card key={group.label} className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <p className="font-display text-sm font-semibold">{group.label}</p>
          </div>
          <ModuleTableHeader />
          {group.items.map((item) => (
            <ModuleRow
              key={item.key}
              label={item.label}
              collection={item.key}
              perms={currentRoleDoc.modules?.[item.key]}
              onChange={(perms) => setModulePerms(item.key, perms)}
              disabled={isSuperAdminRole}
            />
          ))}
        </Card>
      ))}
    </div>
  );
}
